import classNames from 'classnames'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import React, { useCallback, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'

import { IFinancialTableProps, ITableColumn, TTableRow } from './models'

import styles from './FinancialTable.module.scss'

export const FinancialTable = <T,>({
  columns,
  data,
  onCollapse,
  onExpand,
  hierarchyField,
  keyField,
  headerGroups,
  expandedKeys = [],
  className,
  withHierarchy,
}: IFinancialTableProps<T>) => {
  const handleRowToggle = useCallback(
    (row: TTableRow<T>) => {
      const { children: ReactUnused, ...rowWithoutChildren } = row
      const rowChildrenWithoutChildren = (row.children || []).map((el) => {
        const { children: ReactUnused2, ...data } = el
        return data as T
      })

      if (expandedKeys.includes(row[keyField])) {
        onCollapse && onCollapse(rowWithoutChildren as T, rowChildrenWithoutChildren)
      } else {
        onExpand && onExpand(rowWithoutChildren as T, rowChildrenWithoutChildren)
      }
    },
    [expandedKeys, keyField, onCollapse, onExpand, data, hierarchyField]
  )

  const getTdValue = useCallback((column: ITableColumn<T>, row: TTableRow<T>) => {
    if (column.renderFn) {
      return column.renderFn(row)
    }

    return row[column.key]
  }, [])

  const renderRows = useCallback(
    (rows: TTableRow<T>[], deep = 0) => {
      return rows.map((row) => {
        return (
          <React.Fragment key={row[keyField]}>
            <tr>
              <RenderWhen condition={withHierarchy}>
                <td className={styles.toggleCell}>
                  {row.children && row.children.length > 0 && (
                    <button onClick={() => handleRowToggle(row)}>
                      <i
                        className={classNames({
                          'kbase kbase-caret-down': expandedKeys.includes(row[keyField]),
                          'kbase kbase-caret-right': !expandedKeys.includes(row[keyField]),
                        })}
                      />
                    </button>
                  )}
                </td>
              </RenderWhen>

              {columns.map((column, index) => {
                return (
                  <td style={{ width: column.width }} key={`${column.key}:${index}`}>
                    <div style={{ marginLeft: index === 0 ? 16 * deep : 0 }}>
                      {getTdValue(column, row)}
                    </div>
                  </td>
                )
              })}
            </tr>
            {expandedKeys.includes(row[keyField]) &&
              row.children &&
              renderRows(row.children, deep + 1)}
          </React.Fragment>
        )
      })
    },
    [columns, expandedKeys, getTdValue, handleRowToggle, keyField, withHierarchy]
  )

  const buildHierarchy = useCallback(
    (data: T[]): TTableRow<T>[] => {
      const indexedData = data.reduce(
        (acc, item) => {
          acc[item[keyField]] = { ...item, children: [] }
          return acc
        },
        {} as Record<number, TTableRow<T>>
      )

      return data.reduce((result, item) => {
        const parentId = item[hierarchyField]
        const parent = indexedData[parentId]

        if (parent && parent.children) {
          parent.children.push(indexedData[item[keyField]])
        } else {
          result.push(indexedData[item[keyField]])
        }

        return result
      }, [] as TTableRow<T>[])
    },
    [hierarchyField, keyField]
  )

  const groupedData: TTableRow<T>[] = useMemo(() => buildHierarchy(data), [buildHierarchy, data])

  return (
    <table className={classNames(styles.customTable, className)}>
      <thead>
        <RenderWhen condition={Boolean(headerGroups)}>
          <tr>
            {(headerGroups || []).map((group, index) => {
              return (
                <th
                  key={index}
                  colSpan={group.colspan}
                  className={classNames({ [styles.noneBorder]: group.withoutBorder })}
                >
                  {group.name}
                </th>
              )
            })}
          </tr>
        </RenderWhen>

        <tr>
          <RenderWhen condition={withHierarchy}>
            <th className={styles.toggleCell}></th>
          </RenderWhen>

          {columns.map((column, index) => {
            return (
              <th
                key={`${column.key}:${index}`}
                className={classNames(styles.subheaderCell, {
                  [styles.withHierarchy]: withHierarchy,
                })}
                style={{ width: column.width }}
              >
                {column.isTranslatable ? <Translation text={column.title} /> : column.title}
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>{renderRows(groupedData)}</tbody>
    </table>
  )
}
